import type { Product, ProductCategory } from '../types/shop';
import { images } from './images';

// [NEED FROM ABBY: final product list, photos and prices] — seeded placeholders below.
const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

export const shopCategories: ProductCategory[] = ['Jewelry', 'Timepieces', 'Ritual', 'Crystals', 'Home & Altar', 'Gifts'];

export const products: Product[] = [
  {
    id: 'watch',
    slug: 'true-twelve-watch',
    name: 'The True Twelve Watch',
    price: 0,
    categories: ['Timepieces', 'Gifts'],
    imageAlt: 'The True Twelve watch: a minimal dial with XII at the top and twelve gold indices',
    backdrop: 'bg-cream',
    story: 'Built on the twelve. A minimal dial with XII at the top and twelve gold indices, each one a sign, a house and an hour.',
    alignedWith: 'All twelve signs',
    details: ['Coming soon', 'Cream or deep-charcoal face'],
    status: 'coming-soon',
    visual: 'watch',
  },
  {
    id: 'zodiac-pendant',
    slug: 'zodiac-sign-pendant',
    name: 'Zodiac Sign Pendant',
    price: 88,
    categories: ['Jewelry', 'Gifts'],
    image: images.pendant,
    imageAlt: 'Gold coin pendant engraved with a fine-line zodiac glyph on a fine chain',
    backdrop: 'bg-stone',
    story: 'A small gold coin engraved with a single fine-line glyph. Worn close, it is a quiet reminder of the energy you came into the world with.',
    alignedWith: 'Choose your sun, moon or rising sign, whichever feels most like you.',
    details: ['Gold-tone finish', 'Fine cable chain', 'Engraved glyph'],
    option: { label: 'Sign', values: SIGNS },
    status: 'available',
  },
  {
    id: 'geometry-ring',
    slug: 'sacred-geometry-ring',
    name: 'Sacred Geometry Ring',
    price: 120,
    categories: ['Jewelry'],
    image: images.ring,
    imageAlt: 'Slim gold ring engraved with concentric circles and a twelve-point star',
    backdrop: 'bg-stone',
    story: 'Concentric circles and a twelve-point star, engraved so finely they read as texture. The dial, worn on your hand.',
    alignedWith: 'All signs. A daily anchor for intention.',
    details: ['Gold-tone finish', 'Hand-finished engraving', 'Sizes 5–9'],
    option: { label: 'Size', values: ['5', '6', '7', '8', '9'] },
    status: 'available',
  },
  {
    id: 'birth-chart-necklace',
    slug: 'birth-chart-necklace',
    name: 'Birth Chart Necklace',
    price: 145,
    categories: ['Jewelry', 'Gifts'],
    image: images.necklace,
    imageAlt: 'Gold disc pendant engraved with a twelve-segment chart wheel',
    backdrop: 'bg-stone',
    story: 'Your chart wheel, divided into its twelve houses and engraved on a gold disc. A blueprint you can wear.',
    alignedWith: 'Made to your birth details: date, exact time and city.',
    details: ['Gold-tone finish', 'Personalized engraving', 'Ships in a linen pouch'],
    status: 'available',
  },
  {
    id: 'constellation-bracelet',
    slug: 'constellation-bracelet',
    name: 'Constellation Bracelet',
    price: 96,
    categories: ['Jewelry', 'Gifts'],
    image: images.bracelet,
    imageAlt: 'Dainty gold chain bracelet with tiny star stations forming a constellation',
    backdrop: 'bg-lavender/60',
    story: 'Tiny gold stations mapped to the stars of your sign, strung along a delicate chain.',
    alignedWith: 'Choose the constellation of your sign.',
    details: ['Gold-tone finish', 'Adjustable length'],
    option: { label: 'Sign', values: SIGNS },
    status: 'available',
  },
  {
    id: 'zodiac-candle',
    slug: 'zodiac-candle',
    name: 'Zodiac Candle',
    price: 42,
    categories: ['Ritual', 'Home & Altar', 'Gifts'],
    image: images.candle,
    imageAlt: 'Cream ceramic candle vessel with a debossed gold circle, lit',
    backdrop: 'bg-blush/60',
    story: 'One candle for each sign, each with its own scent profile. Light it to open a ritual, a reading or a quiet evening.',
    alignedWith: 'One per sign. Choose yours or gift one.',
    details: ['Reusable ceramic vessel', 'Natural wax blend'],
    option: { label: 'Sign', values: SIGNS },
    status: 'available',
  },
  {
    id: 'incense-set',
    slug: 'ritual-incense-set',
    name: 'Ritual Incense Set',
    price: 36,
    categories: ['Ritual', 'Home & Altar'],
    image: images.incense,
    imageAlt: 'Incense sticks with a round travertine holder and a small brass dish',
    backdrop: 'bg-stone',
    story: 'Hand-rolled incense with a travertine holder and brass dish. For mornings, meditation and clearing the air.',
    alignedWith: 'All signs. Best at dawn or dusk.',
    details: ['Travertine holder', 'Brass dish', '20 sticks'],
    status: 'available',
  },
  {
    id: 'crystal-set',
    slug: 'crystal-set-by-sign',
    name: 'Crystal Set by Sign',
    price: 58,
    categories: ['Crystals', 'Ritual'],
    image: images.crystals,
    imageAlt: 'Rose quartz, clear quartz and amethyst in a shallow cream ceramic tray',
    backdrop: 'bg-sky/70',
    story: 'Three stones chosen for your sign, presented in a ceramic tray for your altar or desk.',
    alignedWith: 'Curated per sign.',
    details: ['Three polished stones', 'Ceramic tray'],
    option: { label: 'Sign', values: SIGNS },
    status: 'available',
  },
  {
    id: 'brass-buddha',
    slug: 'brass-buddha',
    name: 'Brass Buddha',
    price: 165,
    categories: ['Home & Altar'],
    image: images.buddha,
    imageAlt: 'Small brushed brass seated Buddha statue',
    backdrop: 'bg-stone',
    story: 'A brushed brass altar piece to anchor a quiet corner. Calm, weighty and made to last.',
    alignedWith: 'All signs. Place facing east.',
    details: ['Solid brass', 'Brushed finish'],
    status: 'available',
  },
  {
    id: 'gift-card',
    slug: 'true-twelve-gift-card',
    name: 'True Twelve Gift Card',
    price: 50,
    pricePrefix: 'From',
    categories: ['Gifts'],
    imageAlt: 'True Twelve gift card in charcoal with the XII mark',
    backdrop: 'bg-cream',
    story: 'For pieces or readings. Delivered by email, with a note from you.',
    alignedWith: 'Anyone, any sign.',
    details: ['Delivered by email', 'Redeemable for products and readings'],
    option: { label: 'Amount', values: ['$50', '$100', '$200', '$400'] },
    status: 'available',
    visual: 'gift-card',
  },
];

export function getProductPrice(product: Product, option?: string): number {
  if (product.visual === 'gift-card' && option) {
    const n = Number(option.replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : product.price;
  }
  return product.price;
}
